<template>
  <section class="terrain-form-renderer" :class="`terrain-form-renderer--${mode}`" :aria-label="ariaLabel">
    <header v-if="definition.title" class="terrain-form-renderer__header"><h2>{{ definition.title }}</h2><p v-if="definition.description">{{ definition.description }}</p></header>
    <p v-if="mode === 'review'" class="terrain-form-renderer__review-notice">Preguntas exclusivas para revisión.</p>
    <p v-if="!activeSections.length" class="terrain-form-renderer__empty">No hay preguntas para mostrar.</p>
    <section v-for="(section, index) in activeSections" v-show="index === activeSectionIndex" :key="section.sectionId || index" class="terrain-form-renderer__section" :aria-label="section.title || `Sección ${index + 1}`">
      <h3>{{ section.title || `Sección ${index + 1}` }}</h3><p v-if="section.description" class="terrain-form-renderer__section-description">{{ section.description }}</p>
      <TerrainQuestionField
        v-for="question in visibleQuestions(section)"
        :key="question.questionId"
        :question="question"
        :value="answerValue(question.questionId)"
        :readonly="readonly || readonlyQuestionIds.includes(question.questionId)"
        :campaign-id="campaignId"
        :template-version-id="templateVersionId"
        :can-upload-attachments="mode === 'respond'"
        :attachments-editable="attachmentsEditable"
        @input="setAnswer(question.questionId, $event)"
      />
    </section>
    <nav v-if="activeSections.length > 1" class="terrain-form-renderer__navigation" aria-label="Navegación de secciones"><button type="button" :disabled="activeSectionIndex === 0" @click="previousSection">Anterior</button><span>Sección {{ activeSectionIndex + 1 }} de {{ activeSections.length }}</span><button type="button" :disabled="activeSectionIndex === activeSections.length - 1" @click="nextSection">Siguiente</button></nav>
  </section>
</template>

<script>
import TerrainQuestionField from './TerrainQuestionField.vue';
import { evaluateVisibility } from '../../../services/terrain/validation.js';

export default {
  name: 'TerrainFormRenderer',
  components: { TerrainQuestionField },
  props: {
    definition: { type: Object, required: true },
    answers: { type: Array, default: () => [] },
    mode: { type: String, default: 'preview', validator: (value) => ['preview', 'respond', 'review'].includes(value) },
    readonly: { type: Boolean, default: false },
	readonlyQuestionIds: { type: Array, default: () => [] },
    campaignId: { type: String, default: null },
    templateVersionId: { type: String, default: null },
    sanitizeHiddenAnswers: { type: Boolean, default: false },
    // La previsualización administrativa puede mostrar todo el formulario
    // sin tener que contestar primero cada condición. No cambia el flujo del
    // respondente, donde la visibilidad continúa evaluándose normalmente.
    showAllTerrainElements: { type: Boolean, default: false },
  },
  data() { return { activeSectionIndex: 0 }; },
  computed: {
    activeSections() {
      if (this.mode === 'review') return this.definition.reviewSections || [];
      if (this.showAllTerrainElements) return this.definition.sections || [];
      return (this.definition.sections || []).filter((section) => this.visibleTerrainSectionIds.has(section.sectionId));
    },
    ariaLabel() { return this.mode === 'review' ? 'Formulario de revisión' : 'Formulario de terreno'; },
    answerMap() { return this.answers.reduce((map, answer) => ({ ...map, [answer.questionId]: answer.value }), {}); },
    visibility() { return evaluateVisibility(this.definition, this.answers); },
    visibleTerrainSectionIds() { return new Set(this.visibility.visibleSectionIds); },
    visibleTerrainQuestionIds() { return new Set(this.visibility.visibleQuestionIds); },
    shouldSanitizeHiddenAnswers() { return this.mode === 'respond' || this.sanitizeHiddenAnswers; },
    // Solo la vista de detalle (preview, sin canUpload) admite "Editar
    // adjuntos" — mismo criterio que ya activa canEditDetail en
    // TerrainReviewQueue.vue (mode siempre es "preview" ahí; readonly viaja
    // como !canEditDetail). El modo respond ya sube inline (AttachmentEditor
    // sin chrome), y review nunca tiene preguntas file/photo.
    attachmentsEditable() { return this.mode === 'preview' && !this.readonly; },
  },
  watch: {
    activeSections() { if (this.activeSectionIndex >= this.activeSections.length) this.activeSectionIndex = Math.max(0, this.activeSections.length - 1); },
    answers: { deep: true, handler() { this.sanitizeAnswers(); } },
    definition: { deep: true, handler() { this.sanitizeAnswers(); } },
  },
  methods: {
    visibleQuestions(section) {
      if (this.mode === 'review' || this.showAllTerrainElements) return section.questions || [];
      return (section.questions || []).filter((question) => this.visibleTerrainQuestionIds.has(question.questionId));
    },
    answerValue(questionId) { return this.answerMap[questionId]; },
    setAnswer(questionId, value) {
      const next = [...this.answers.filter((answer) => answer.questionId !== questionId), { questionId, value }];
      const visible = new Set(evaluateVisibility(this.definition, next).visibleQuestionIds);
      const cleaned = this.shouldSanitizeHiddenAnswers ? next.filter((answer) => visible.has(answer.questionId)) : next;
      this.$emit('update:answers', cleaned);
      this.$emit('answer-change', { questionId, value });
    },
    sanitizeAnswers() {
      if (!this.shouldSanitizeHiddenAnswers || this.mode === 'review') return;
      const visible = new Set(evaluateVisibility(this.definition, this.answers).visibleQuestionIds);
      const cleaned = this.answers.filter((answer) => visible.has(answer.questionId));
      if (cleaned.length !== this.answers.length) this.$emit('update:answers', cleaned);
    },
    previousSection() { this.activeSectionIndex = Math.max(0, this.activeSectionIndex - 1); },
    nextSection() { this.activeSectionIndex = Math.min(this.activeSections.length - 1, this.activeSectionIndex + 1); },
  },
};
</script>

<style scoped lang="scss">
.terrain-form-renderer {
  box-sizing: border-box;
  display: grid;
  gap: var(--terrain-space-lg);
  margin: 0 auto;
  max-width: 760px;
  min-width: 0;
  overflow-x: hidden;
  width: 100%;
}

.terrain-form-renderer__header h2, .terrain-form-renderer__section h3 { color: var(--color-navy); margin: 0; }
.terrain-form-renderer__header p, .terrain-form-renderer__section-description { color: var(--color-ghost); margin: var(--terrain-space-xs) 0 0; }
.terrain-form-renderer__section { display: grid; gap: var(--terrain-space-md); }

.terrain-form-renderer__review-notice {
  background: var(--color-mist);
  border-left: 4px solid var(--color-steel);
  border-radius: var(--terrain-radius-sm);
  color: var(--color-navy);
  margin: 0;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
}

.terrain-form-renderer__empty {
  border: 1px dashed var(--color-line);
  border-radius: var(--terrain-radius-lg);
  color: var(--color-ghost);
  margin: 0;
  padding: var(--terrain-space-lg);
  text-align: center;
}

.terrain-form-renderer__navigation { align-items: center; display: flex; gap: var(--terrain-space-md); justify-content: space-between; }

.terrain-form-renderer__navigation button {
  background: var(--color-navy);
  border: 0;
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  transition: background-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-steel); }
  &:disabled { background: var(--color-ghost); cursor: not-allowed; }
}
</style>
